import {ChevronRightIcon, DashIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState, type ReactNode} from 'react'

import styles from './UserChoiceCard.module.css'

export interface UserChoiceCardProps {
  /** Card title shown in the header. */
  title: ReactNode
  /** Optional muted text shown below the title. */
  subtitle?: ReactNode
  /** Additional controls rendered in the header. */
  headerButtons?: ReactNode
  /** Preview content rendered above the choices. */
  previewContent?: ReactNode
  /** Choice rows or other interactive content. */
  children?: ReactNode
  /** Controls pinned below the scrollable card content. */
  footerButtons?: ReactNode
  /** Removes the outer card chrome when nested inside another card. */
  embedded?: boolean
  /** Controlled expanded state. */
  isExpanded?: boolean
  /** Called when the expand or collapse control is activated. */
  onToggle?: () => void
}

/** Card wrapper for active interactive user prompts. */
export function UserChoiceCard({
  title,
  subtitle,
  headerButtons,
  previewContent,
  children,
  footerButtons,
  embedded = false,
  isExpanded: isExpandedProp,
  onToggle,
}: UserChoiceCardProps) {
  const [isExpandedInternal, setIsExpandedInternal] = useState(true)
  const isControlled = isExpandedProp !== undefined
  const isExpanded = isControlled ? isExpandedProp : isExpandedInternal
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)
  const [canScroll, setCanScroll] = useState(false)

  const checkScroll = useCallback(() => {
    const element = scrollRef.current
    if (!element) return

    setIsScrolled(element.scrollTop > 0)
    setCanScroll(element.scrollHeight > element.clientHeight)
    setIsScrolledToBottom(element.scrollTop + element.clientHeight >= element.scrollHeight - 1)
  }, [])

  useEffect(() => {
    checkScroll()
  }, [checkScroll, isExpanded])

  const handleToggle = () => {
    if (!isControlled) setIsExpandedInternal(current => !current)
    onToggle?.()
  }

  const header = !embedded ? (
    <div className={styles.Header}>
      <div className={styles.HeaderText}>
        <div className={styles.Title}>{title}</div>
        {subtitle ? <div className={styles.Subtitle}>{subtitle}</div> : null}
      </div>
      <div className={styles.HeaderActions}>
        {headerButtons}
        <button
          type="button"
          className={styles.CollapseButton}
          onClick={handleToggle}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
        >
          {isExpanded ? <DashIcon size={16} /> : <ChevronRightIcon size={16} />}
        </button>
      </div>
    </div>
  ) : null

  return (
    <div className={clsx(styles.Card, embedded && styles.embedded)}>
      <div ref={scrollRef} className={styles.ScrollArea} onScroll={checkScroll}>
        {header}
        {isExpanded ? (
          <>
            {canScroll && isScrolled ? <div className={styles.GradientTop} /> : null}
            {previewContent ? <div className={styles.PreviewContent}>{previewContent}</div> : null}
            {children ? <div className={styles.ChildrenContent}>{children}</div> : null}
            {canScroll && !isScrolledToBottom ? <div className={styles.GradientBottom} /> : null}
          </>
        ) : null}
      </div>
      {isExpanded && footerButtons ? (
        <div className={clsx(styles.Footer, canScroll && styles.footerBorder)}>
          <div className={styles.FooterButtons}>{footerButtons}</div>
        </div>
      ) : null}
    </div>
  )
}
